import { ContentRegistry, loadContentRegistry } from '../data';
import { PlayerProfile, ProfileSettings, RunSummary } from '../data/ProfileState';
import { createLogger, Logger } from '../utils/Logger';
import { ProfileSnapshotResult, ProfileStorageLike, ProfileStorageManager } from './ProfileStorageManager';

export interface ProfileRuntimeOptions {
  readonly storage?: ProfileStorageLike | null;
  readonly storageKey?: string;
  readonly contentRegistry?: ContentRegistry;
  readonly logger?: Logger;
}

export class ProfileRuntime {
  private readonly storageManager: ProfileStorageManager;
  private readonly logger: Logger;
  private readonly settingsListeners = new Set<(settings: ProfileSettings) => void>();

  constructor(options: ProfileRuntimeOptions = {}) {
    this.logger = options.logger ?? createLogger('ProfileRuntime');
    this.storageManager = new ProfileStorageManager({
      storage: options.storage,
      storageKey: options.storageKey,
      contentRegistry: options.contentRegistry,
      logger: this.logger
    });
    this.logger.debug('constructor:complete');
  }

  public loadProfile(): PlayerProfile {
    const result = this.storageManager.loadProfile();
    if (!result.ok) {
      this.logger.warn('loadProfile:fallback-profile-used', { error: result.error });
    }
    return result.profile;
  }

  public getProfileSnapshot(): ProfileSnapshotResult {
    this.logger.debug('getProfileSnapshot:start');
    return this.storageManager.getSnapshot();
  }

  public clearProfileState(): ProfileSnapshotResult {
    this.logger.debug('clearProfileState:start');
    return this.storageManager.clearProfile();
  }

  public updateSettings(patch: Partial<ProfileSettings>) {
    this.logger.debug('updateSettings:start', { keys: Object.keys(patch) });
    const result = this.storageManager.updateSettings(patch);
    this.settingsListeners.forEach(listener => listener(result.profile.settings));
    this.logger.debug('updateSettings:applied', { saved: result.ok });
    return result;
  }

  public onSettingsChanged(listener: (settings: ProfileSettings) => void): () => void {
    this.settingsListeners.add(listener);
    listener(this.storageManager.getProfile().settings);
    return () => { this.settingsListeners.delete(listener); };
  }

  public commitRun(summary: RunSummary) {
    this.logger.debug('commitRun:start', { result: summary.result });
    return this.storageManager.commitRun(summary);
  }

  public unlockWeapon(weaponId: string) {
    this.logger.debug('unlockWeapon:start', { weaponId });
    return this.storageManager.unlockWeapon(weaponId);
  }

  public resolveWeaponUnlocks() {
    return this.storageManager.resolveWeaponUnlocks();
  }
}

let sharedRuntime: ProfileRuntime | null = null;

export function getSharedProfileRuntime(options: ProfileRuntimeOptions = {}): ProfileRuntime {
  if (!sharedRuntime) {
    sharedRuntime = new ProfileRuntime({
      contentRegistry: options.contentRegistry ?? loadContentRegistry(),
      ...options
    });
    createLogger('ProfileRuntime').debug('getSharedProfileRuntime:created');
  }
  return sharedRuntime;
}

export function resetSharedProfileRuntimeForTests(): void {
  sharedRuntime = null;
}
